import { User } from '@supabase/supabase-js';
import { supabase } from './supabase';

// The vetted AI models offered in the company AI-model dropdown (active only, in display order).
export const fetchAiModels = async () => {
  const { data, error } = await supabase
    .from('ai_models')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('model', { ascending: true });

  if (error) throw error;
  return data || [];
};

// Super-admin only (also enforced by RLS).
export const addAiModel = async (
  model: string,
  label: string,
  supportedParameters: string[] | null = null
) => {
  const { error } = await supabase.from('ai_models').insert({
    model: model.trim(),
    label: label.trim() || null,
    supported_parameters: supportedParameters,
  });

  if (error) throw error;
};

// Super-admin only (also enforced by RLS).
export const removeAiModel = async (model: string) => {
  const { error } = await supabase.from('ai_models').delete().eq('model', model);

  if (error) throw error;
};

export const fetchCompaniesData = async () => {
  const { data: companiesData, error: companiesError } = await supabase
    .from('companies')
    .select('*')
    .order('name', { ascending: true });

  if (companiesError) throw companiesError;
  return companiesData || [];
};

export const fetchDomainsForCompany = async (companyId: string) => {
  const { data: domainsData, error: domainsError } = await supabase
    .from('domains')
    .select('*')
    .eq('company_id', companyId)
    .eq('active', true)
    .order('domain', { ascending: true });

  if (domainsError) throw domainsError;
  return domainsData || [];
};

export const fetchDomainRelatedData = async (domainId: string) => {
  const [targetGroupsResponse, brandsResponse] = await Promise.all([
    supabase
      .from('customer_target_groups')
      .select('*')
      .eq('domain_id', domainId)
      .order('name', { ascending: true }),
    supabase
      .from('brands')
      .select('*')
      .eq('domain_id', domainId)
      .order('name', { ascending: true }),
  ]);

  if (targetGroupsResponse.error) throw targetGroupsResponse.error;
  if (brandsResponse.error) throw brandsResponse.error;

  return {
    customerTargetGroups: targetGroupsResponse.data || [],
    brands: brandsResponse.data || [],
  };
};

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const createNewChat = async (
  name: string,
  customerTargetGroupId: string,
  companyId: string,
  domainId: string
) => {
  const { data: chat, error } = await supabase
    .from('chats')
    .insert({
      name,
      customer_target_group_id: customerTargetGroupId,
      company_id: companyId,
      domain_id: domainId,
    })
    .select()
    .single();

  if (error) throw error;
  return chat;
};

export const saveMessage = async (chatId: string, message: ChatMessage) => {
  const { error } = await supabase.from('messages').insert({
    chat_id: chatId,
    sender: message.role === 'user' ? 'user' : 'assistant',
    message: JSON.stringify(message),
  });

  if (error) throw error;
};

export const getChatMessages = async (chatId: string): Promise<ChatMessage[]> => {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('chat_id', chatId)
    .order('created_at', { ascending: true });

  if (error) throw error;

  return (data || []).map((msg) => JSON.parse(msg.message));
};

export const fetchWishlist = async () => {
  const { data: wishlistData, error: wishlistError } = await supabase
    .from('user_feedback')
    .select('*');

  if (wishlistError) throw wishlistError;
  return wishlistData || [];
};

export const updateWishlistItemStatus = async (id: number, status: string) => {
  // Add a small delay to simulate network latency
  await new Promise((resolve) => setTimeout(resolve, 800));

  const { error } = await supabase.from('user_feedback').update({ status }).eq('id', id);

  if (error) throw error;
  return true;
};

export const deleteWishlistItem = async (id: number) => {
  // Add a small delay to simulate network latency
  await new Promise((resolve) => setTimeout(resolve, 800));

  const { error } = await supabase.from('user_feedback').delete().eq('id', id);

  if (error) throw error;
  return true;
};

export interface SavePromptParams {
  name: string;
  content: string;
  feature: string;
  companyId: string;
  domainId?: string;
  private?: boolean;
  userId?: string;
  creatorDetails?: object;
}

export const savePrompt = async (params: SavePromptParams) => {
  const {
    name,
    content,
    feature,
    companyId,
    domainId,
    private: isPrivate = true,
    userId,
    creatorDetails,
  } = params;

  const { data, error } = await supabase
    .from('prompts')
    .insert({
      name,
      content,
      feature,
      company_id: companyId,
      domain_id: domainId,
      private: isPrivate,
      user_id: userId,
      creator_details: creatorDetails,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export interface CompanyMessage {
  id: string;
  company_id: string;
  creator_details: string;
  message: string;
  type: 'info' | 'notice' | 'warning';
  validity_range: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  dismissed?: boolean;
  dismissed_at?: string;
}

export const fetchAllCompanyMessages = async (companyId: string): Promise<CompanyMessage[]> => {
  const { data: messages, error: messagesError } = await supabase
    .from('company_messages')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });
  if (messagesError) throw messagesError;
  return messages || [];
};

export const fetchCompanyMessages = async (companyId: string): Promise<CompanyMessage[]> => {
  try {
    // First, get all valid messages for the company
    const { data: messages, error: messagesError } = await supabase
      .from('company_messages')
      .select('*')
      .eq('company_id', companyId)
      .filter('validity_range', 'cs', `[${new Date().toISOString()},${new Date().toISOString()}]`)
      .order('created_at', { ascending: false });

    if (messagesError) throw messagesError;
    if (!messages || messages.length === 0) return [];

    // Get current date at start of day (midnight) for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // For each message, get its dismissal status
    const messagesWithDismissalInfo = await Promise.all(
      messages.map(async (message) => {
        const { data: mappings, error: mappingError } = await supabase
          .from('company_message_user_mapping')
          .select('dismissed, dismissed_at')
          .eq('company_message_id', message.id)
          .maybeSingle();

        if (mappingError) throw mappingError;

        // Show the message if:
        // 1. It's not dismissed, OR
        // 2. It's a warning AND (it's not dismissed OR it was dismissed before today)
        if (
          !mappings ||
          !mappings.dismissed ||
          (message.type === 'warning' &&
            (!mappings.dismissed_at || new Date(mappings.dismissed_at) < today))
        ) {
          return {
            ...message,
            dismissed: mappings?.dismissed || false,
            dismissed_at: mappings?.dismissed_at || null,
          };
        }

        return null;
      })
    );

    // Filter out null values (dismissed non-warning messages)
    return messagesWithDismissalInfo.filter(Boolean) as CompanyMessage[];
  } catch (error) {
    console.error('Error fetching company messages:', error);
    throw error;
  }
};

export const createCompanyMessage = async (
  companyId: string,
  messageData: Omit<
    CompanyMessage,
    'id' | 'company_id' | 'creator_details' | 'created_by' | 'created_at' | 'updated_at'
  >,
  user: User
): Promise<CompanyMessage> => {
  const { data, error } = await supabase
    .from('company_messages')
    .insert({
      company_id: companyId,
      message: messageData.message,
      type: messageData.type,
      validity_range: messageData.validity_range,
      creator_details: {
        user_name: user.user_metadata.first_name + ' ' + user.user_metadata.last_name,
        user_email: user.email,
      },
      created_by: user.id,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateCompanyMessage = async (
  messageId: string,
  messageData: Partial<
    Omit<
      CompanyMessage,
      'id' | 'company_id' | 'creator_details' | 'created_by' | 'created_at' | 'updated_at'
    >
  >
): Promise<CompanyMessage> => {
  const updateData: {
    message?: string;
    type?: 'info' | 'notice' | 'warning';
    validity_range?: string;
  } = {};

  if (messageData.message) updateData.message = messageData.message;
  if (messageData.type) updateData.type = messageData.type;
  if (messageData.validity_range) {
    updateData.validity_range = messageData.validity_range;
  }

  const { data, error } = await supabase
    .from('company_messages')
    .update(updateData)
    .eq('id', messageId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteCompanyMessage = async (messageId: string) => {
  const { error } = await supabase.from('company_messages').delete().eq('id', messageId);
  if (error) throw error;
  return true;
};

export const dismissCompanyMessage = async (messageId: string): Promise<boolean> => {
  const { error } = await supabase.rpc('mark_company_message_dismissed', {
    p_message_id: messageId,
  });

  if (error) throw error;
  return true;
};

// Re-export supabase for backward compatibility - prefer importing from './supabase' directly
export { supabase };
